import { Dispatch, FC, Fragment, SetStateAction, useMemo, useState } from 'react';
import { generalBlue, googleMapConfig } from 'src/app-config';
import { GoogleMap, Polygon, useJsApiLoader } from '@react-google-maps/api';
import { HTHActivityFaceActivity } from './HTHActivity5FaceActivity';
import { hthHelper } from 'src/services';
import { hthMapStyle, HTHMarkerBuilding, HTHPolygonInfoBox } from 'src/components/_commons/house-to-house';
import { Loading } from 'src/components/_commons';
import { typeHTHBuilding, typeHTHTerritory, typeHTHFace, typePublicMinistryMonthlyService, typePublicMinistryService } from '@monorepo/models';
import { useGetConfig, useGetIsMobile } from 'src/custom-hooks';

type propsType = {
    completionDate: number;
    day?: number;
    everyPolygonIsComplete: boolean;
    hthTerritory: typeHTHTerritory;
    monthlyService: typePublicMinistryMonthlyService;
    service: typePublicMinistryService;
    setAllHandler: () => void;
    setHthTerritories: Dispatch<SetStateAction<typeHTHTerritory[] | null>>;
};

export const HTHActivityPolygons: FC<propsType> = ({
    completionDate, day, everyPolygonIsComplete, hthTerritory,
    monthlyService, service, setAllHandler, setHthTerritories
}) => {
    const [polygonId, setPolygonId] = useState(0);
    const config = useGetConfig();
    const isMobile = useGetIsMobile();
    const mapLoader = useJsApiLoader(googleMapConfig);

    const [tt] = useMemo(() =>
        hthHelper.SetIsCoveredAndColorForHTHPolygons([hthTerritory], service.id, completionDate)
    , [hthTerritory, service]);

    const getBuildings = (polygon: typeHTHFace): typeHTHBuilding[] => {
        let hs: typeHTHBuilding[] = [];
        polygon.buildings
            ?.filter(b => b.households
                .some(h => h.history.filter(h => !!h.state)
                .map(d => d.timestamp)
                .some(d => {
                    const _date = new Date(d);
                    if (day) {
                        return _date.getFullYear() === monthlyService.year
                            && _date.getMonth() === monthlyService.month
                            && _date.getDate() === day
                        ;
                    } else {
                        return _date.getFullYear() === monthlyService.year
                            && _date.getMonth() === monthlyService.month
                        ;
                    }
                }))
            ).forEach(b => {
                if (!!b.coords) {
                    const x = { ...b, street: polygon.street, territoryNumber: hthTerritory.territoryNumber };
                    hs.push(x);
                }
            })
        ;
        return hs;
    }

    const updatePolygonIsCovered = (polygonId: number) => {
        setHthTerritories(x => x!.map(_t => _t.number === hthTerritory.number ? ({
            ..._t,
            faces: _t.faces.map(_p => _p.id === polygonId ? ({
                ..._p,
                serviceDates: _p.serviceDates.map(sd => sd.serviceId).includes(service.id) ?
                    _p.serviceDates ?
                        _p.serviceDates.filter(sd => sd.serviceId !== service.id)
                        :
                        []
                    :
                    _p.serviceDates ?
                        [..._p.serviceDates, { completionDate, serviceId: service.id }]
                        :
                        [{ completionDate, serviceId: service.id }]
            }) : _p)
        }) : _t));
    }

    return (
        <div className={'row'}>
            <div className={'col-lg-4'} style={{ order: isMobile ? 2 : 1 }}>
                <button className={`btn btn-sm ${everyPolygonIsComplete ? 'btn-general-blue' : 'btn-general-red'} w-100 px-3 ${isMobile ? 'mt-3' : ''}`}
                    style={{ marginBottom: '12px' }}
                    onClick={setAllHandler}
                >
                    {everyPolygonIsComplete ? 'Desmarcar todo' : 'Marcar todo'}
                </button>
                <HTHActivityFaceActivity
                    service={service}
                    tt={tt}
                />
            </div>
            <div className={'col-lg-8'} style={{ order: isMobile ? 1 : 2 }}>
                {mapLoader.isLoaded ?
                    <div className={'position-relative'} >
                        <GoogleMap
                            id={googleMapConfig.id}
                            mapContainerClassName={'d-block m-auto'}
                            mapContainerStyle={{
                                height: isMobile ? '600px' : '500px',
                                width: isMobile ? '100%' : '90%'
                            }}
                            onLoad={mapInstance => {
                                mapInstance.setCenter(tt.centerCoords);
                                mapInstance.setZoom(tt.zoom);
                            }}
                            options={{
                                minZoom: 8,
                                streetViewControl: true,
                                styles: hthMapStyle,
                                zoomControl: true,
                                zoomControlOptions: { position: google.maps.ControlPosition.LEFT_BOTTOM }
                            }}
                        >
                            {tt.faces.map(polygon =>
                                <Fragment key={polygon.id}>
                                    <Polygon
                                        onClick={() => updatePolygonIsCovered(polygon.id)}
                                        onMouseOut={() => setPolygonId(0)}
                                        onMouseOver={() => setPolygonId(polygon.id)}
                                        options={{
                                            clickable: true,
                                            fillColor: polygon.color ?? generalBlue,
                                            fillOpacity: 0.92,
                                            strokeColor: '',
                                            strokeOpacity: 0.9,
                                            strokePosition: google.maps.StrokePosition.CENTER,
                                            strokeWeight: 10
                                        }}
                                        path={[
                                            polygon.coordsPoint1,
                                            polygon.coordsPoint2,
                                            polygon.coordsPoint3
                                        ]}
                                    />
                                    {polygonId === polygon.id &&
                                        <HTHPolygonInfoBox
                                            polygon={polygon}
                                            territoryNumber={tt.number}
                                            usingLettersForBlocks={config.isUsingLettersForBlocks}
                                            onClickHandler={() => {
                                                updatePolygonIsCovered(polygon.id);
                                            }}
                                        />
                                    }
                                    {getBuildings(polygon).map((b, i) =>
                                        <HTHMarkerBuilding key={i}
                                            building={b}
                                            onClickHandler={() => {
                                                updatePolygonIsCovered?.(polygon.id);
                                            }}
                                        />
                                    )}
                                </Fragment>
                            )}
                        </GoogleMap>
                    </div>
                    :
                    <Loading mt={'40px'} />
                }
            </div>
        </div>
    );
}
